import FVector from '@data/geometry/FVector';
import { FacadeComponentType, FacadeType, FloorBasedFacadeState } from '@visualizer/types';
import { SensorData } from 'Simulations/SensorPoint';
import * as THREE from 'three';
import { tagDesignSchemeSurfaceMaterial } from '../../DesignSchemeSurfaceMaterialHelper';
import { Facade } from './Facade';
import { HorizontallyOrientedFacade } from './FacadeHorizontallyOriented';
import { TransparentMainDirectionFacade } from './FacadeTransparentMainDirection';
import { FacadeWithOpenings } from './FacadeWithOpenings';

type FacadeMaterials = {
    opaqueFacade: THREE.MeshBasicMaterial;
    transparentFacade: THREE.MeshBasicMaterial;
    transparentFacadeFast: THREE.ShaderMaterial;
    verticalFacade: THREE.MeshBasicMaterial;
    verticalFacadeSecondary: THREE.MeshBasicMaterial;
    horizontalOpaqueFacade: THREE.MeshBasicMaterial;
};

export type FacadeRing = {
    perimeter: FVector[];
    facades: Facade[];
    componentContextByType?: Partial<
        Record<
            FacadeComponentType,
            {
                facingIndices: Set<number>;
                adjacentIndices: Set<number>;
                renderedEdgeIndices: Set<number>;
            }
        >
    >;
};

export type FacadeBuildParams = {
    sensors: SensorData[];
    p1: FVector;
    p2: FVector;
    perimeter: FVector[];
    facadeConfig: FloorBasedFacadeState;
    height: number;
    thickness: number;
    direction: FVector | undefined;
    visibilityLoops?: FVector[][];
    isGroundFloor?: boolean;
};

export class FacadeBuilder {
    /**
     * Centralized entrypoint for single-edge facade generation.
     */
    static buildFacade(params: FacadeBuildParams): Facade {
        const materials = FacadeBuilder.createMaterials();
        const visibilityLoops = params.visibilityLoops ?? [params.perimeter];

        switch (params.facadeConfig.type) {
            case FacadeType.TRANSPARENT:
                return FacadeBuilder.buildTransparent(params, materials);
            case FacadeType.OPAQUE:
                return FacadeBuilder.buildOpaque(params, materials);
            case FacadeType.TRANSPARENT_MAIN_DIRECTION:
                return FacadeBuilder.buildTransparentMainDirection(params, materials, visibilityLoops);
            case FacadeType.FACADE_WITH_OPENINGS:
                return new FacadeWithOpenings(
                    params.p1,
                    params.p2,
                    params.height,
                    materials.opaqueFacade,
                    params.facadeConfig.stacking ? materials.transparentFacade : materials.transparentFacadeFast,
                    params.sensors
                );
            case FacadeType.HORIZONTALLY_ORIENTED:
                return FacadeBuilder.buildHorizontallyOriented(params, materials);
            default:
                return FacadeBuilder.buildTransparent(params, materials);
        }
    }

    /**
     * Fully glazed facades.
     */
    private static buildTransparent(params: FacadeBuildParams, materials: FacadeMaterials): Facade {
        return new Facade(
            params.p1,
            params.p2,
            params.height,
            params.facadeConfig.stacking ? materials.transparentFacade : materials.transparentFacadeFast
        );
    }

    private static buildOpaque(params: FacadeBuildParams, materials: FacadeMaterials): Facade {
        return new Facade(params.p1, params.p2, params.height, materials.opaqueFacade);
    }

    /**
     * Glazed only when the edge faces the resolved direction, opaque otherwise.
     */
    private static buildTransparentMainDirection(
        params: FacadeBuildParams,
        materials: FacadeMaterials,
        visibilityLoops: FVector[][]
    ): Facade {
        return new TransparentMainDirectionFacade(
            params.p1,
            params.p2,
            params.height,
            params.facadeConfig.stacking ? materials.transparentFacade : materials.transparentFacadeFast,
            materials.opaqueFacade,
            params.perimeter,
            visibilityLoops,
            params.sensors,
            params.direction
        );
    }

    /**
     * Horizontal facade variant with optional exposed-base closing geometry.
     */
    private static buildHorizontallyOriented(params: FacadeBuildParams, materials: FacadeMaterials): Facade {
        return new HorizontallyOrientedFacade(
            params.p1,
            params.p2,
            params.height + params.thickness,
            params.thickness,
            materials.verticalFacade,
            materials.horizontalOpaqueFacade,
            params.facadeConfig.stacking ? materials.transparentFacade : materials.transparentFacadeFast,
            params.perimeter,
            params.facadeConfig.horizontalLength.value,
            params.isGroundFloor ?? false,
            params.sensors,
            params.direction
        );
    }

    private static createMaterials(): FacadeMaterials {
        const materials: FacadeMaterials = {
            opaqueFacade: new THREE.MeshBasicMaterial({
                color: 0x888888,
                side: THREE.DoubleSide,
                polygonOffset: true,
                polygonOffsetFactor: 1,
                polygonOffsetUnits: 1,
            }),
            transparentFacade: new THREE.MeshBasicMaterial({
                color: 0xadd8e6,
                side: THREE.DoubleSide,
                transparent: true,
                opacity: 0.5,
                polygonOffset: true,
                polygonOffsetFactor: 1,
                polygonOffsetUnits: 1,
            }),
            transparentFacadeFast: new THREE.ShaderMaterial({
                side: THREE.DoubleSide,
                transparent: false,
                depthWrite: true,
                depthTest: true,
                polygonOffset: true,
                polygonOffsetFactor: 1,
                polygonOffsetUnits: 1,
                uniforms: {
                    color: { value: new THREE.Color(0xadd8e6) },
                },
                vertexShader: `
                    void main() {
                        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                    }
                `,
                fragmentShader: `
                    uniform vec3 color;

                    void main() {
                        // Checkerboard pattern:
                        int x = int(gl_FragCoord.x);
                        int y = int(gl_FragCoord.y);

                        if ((x + y) % 2 == 0) {
                            discard;
                        }

                        gl_FragColor = vec4(color, 1.0);
                    }
                `,
            }),
            verticalFacade: new THREE.MeshBasicMaterial({
                color: 0xffd966,
                side: THREE.DoubleSide,
                polygonOffset: true,
                polygonOffsetFactor: 1,
                polygonOffsetUnits: 1,
            }),
            verticalFacadeSecondary: new THREE.MeshBasicMaterial({
                color: 0xffec99,
                side: THREE.DoubleSide,
                polygonOffset: true,
                polygonOffsetFactor: 1,
                polygonOffsetUnits: 1,
            }),
            horizontalOpaqueFacade: new THREE.MeshBasicMaterial({
                color: 0x808080,
                side: THREE.DoubleSide,
                polygonOffset: true,
                polygonOffsetFactor: 1,
                polygonOffsetUnits: 1,
            }),
        };

        tagDesignSchemeSurfaceMaterial(materials.opaqueFacade, 'opaque');
        tagDesignSchemeSurfaceMaterial(materials.transparentFacade, 'glazed');
        tagDesignSchemeSurfaceMaterial(materials.transparentFacadeFast, 'glazed');
        tagDesignSchemeSurfaceMaterial(materials.verticalFacade, 'pv');
        tagDesignSchemeSurfaceMaterial(materials.verticalFacadeSecondary, 'pv');
        tagDesignSchemeSurfaceMaterial(materials.horizontalOpaqueFacade, 'opaque');

        return materials;
    }
}
